import React, { useEffect, useMemo, useState } from "react";

import RightworkSelect from "../../../../../../components/shared/RightworkSelect";
import { getRightworkAPI } from "../../../../requests/utilities/requests";
import { getBusinessWeekStartDayIndex } from "../../../dateRangePresets";
import { getEligibleStoresForReport } from "../../reportRegistry";
import { unwrapPayload } from "./laborMath";

// Shared scope for Report Library reports that cover every store the user
// manages in one company (the labor recaps, the Weekly Labor Summary): which
// company, which store anchors business days and weeks, the store/district
// filter, and the fetch. The backend half is store_info/views/report_scope.py.

const ALL_STORES = { key: "", storeIds: "" };

export function useCompanyReportScope({ report, homeStore, homeCompany, storeNames, memberships }) {
  // A company picked in the dropdown (or the backend's fallback) applies only
  // under the home company it was picked from, so a home-location change is
  // reflected in the very render that sees it, with no stale request.
  const [pickedCompany, setPickedCompany] = useState(null);
  const companyId =
    pickedCompany && pickedCompany.homeCompany === homeCompany
      ? pickedCompany.id
      : homeCompany;
  const setCompanyId = (id) => setPickedCompany({ homeCompany, id });

  // Business days and weeks are per company: anchor them on a report-eligible
  // store in the selected company (the home store when it is one), so an
  // employee-only or inactive store can't set the period.
  const anchorStoreId = useMemo(() => {
    const inCompany = getEligibleStoresForReport(report, storeNames, memberships).filter(
      (s) => Number(s.company) === Number(companyId)
    );
    return (inCompany.find((s) => s.id === homeStore) || inCompany[0])?.id ?? homeStore;
  }, [report, storeNames, memberships, companyId, homeStore]);
  const weekStartDayIndex = useMemo(
    () => getBusinessWeekStartDayIndex(storeNames, anchorStoreId),
    [storeNames, anchorStoreId]
  );

  // key is the dropdown value ("" = every store the user manages in the
  // company, "district:<name>", or "store:<id>"); storeIds is what's sent.
  // A filter belongs to the company it was picked in and is ignored under
  // any other, so switching companies never sends another company's stores.
  const [pickedStoreFilter, setPickedStoreFilter] = useState(null);
  const storeFilter =
    pickedStoreFilter && pickedStoreFilter.companyId === companyId
      ? pickedStoreFilter
      : ALL_STORES;
  const setStoreFilter = (key, storeIds) =>
    setPickedStoreFilter({ companyId, key, storeIds });

  // The backend falls back to a company the user can see.
  const acceptPayloadCompany = (data) => {
    if (data?.company_id && data.company_id !== companyId) {
      setPickedCompany({ homeCompany, id: data.company_id });
    }
  };

  return {
    companyId,
    setCompanyId,
    anchorStoreId,
    weekStartDayIndex,
    storeFilter,
    setStoreFilter,
    acceptPayloadCompany,
  };
}

// Fetches the report whenever its params change. params is compared by value,
// so a new-but-equal object never refetches; null params skip the request.
export function useScopedReportFetch(endpoint, params, { onData, notFoundMessage, errorMessage }) {
  const [payload, setPayload] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const paramsKey = params ? JSON.stringify(params) : null;

  useEffect(() => {
    if (!paramsKey) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await getRightworkAPI(endpoint, JSON.parse(paramsKey));
        if (cancelled) return;
        const data = unwrapPayload(res);
        setPayload(data);
        onData?.(data);
      } catch (e) {
        if (cancelled) return;
        setPayload(null);
        setError(e?.response?.status === 404 ? notFoundMessage : errorMessage);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // onData and the messages are per-render closures and copy; refetching
    // follows the endpoint and params only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, paramsKey, retryNonce]);

  return { payload, isLoading, error, retry: () => setRetryNonce((n) => n + 1) };
}

// The stores the Stores filter offers: the payload's, but only once it is for
// the selected company, so a pick while another company loads can't send one
// company's store IDs with another's company_id.
export function scopedStoreOptions(payload, companyId) {
  const isCurrent = payload != null && Number(payload.company_id) === Number(companyId);
  return isCurrent ? payload?.stores || [] : [];
}

export function ReportScopeFilters({ idPrefix, payload, scope }) {
  const { companyId, setCompanyId, storeFilter, setStoreFilter } = scope;
  const companyOptions = payload?.company_options || [];
  const storeOptions = scopedStoreOptions(payload, companyId);
  const districtNames = [
    ...new Set(storeOptions.map((s) => s.district_name).filter(Boolean)),
  ].sort();
  // Districts only help when there's more than one to pick; the section
  // labels are disabled options since the shared select has no subheaders.
  const showDistricts = districtNames.length > 1;
  const storeFilterOptions = [
    { value: "", label: "All my stores" },
    ...(showDistricts
      ? [
          { value: "section:districts", label: "Districts", disabled: true },
          ...districtNames.map((name) => ({ value: `district:${name}`, label: name })),
          { value: "section:stores", label: "Stores", disabled: true },
        ]
      : []),
    ...storeOptions.map((st) => ({ value: `store:${st.id}`, label: st.store_name })),
  ];
  const handleStoreFilterChange = (key) => {
    let ids = [];
    if (key.startsWith("district:")) {
      const district = key.slice("district:".length);
      ids = storeOptions.filter((s) => s.district_name === district).map((s) => s.id);
    } else if (key.startsWith("store:")) {
      ids = [Number(key.slice("store:".length))];
    }
    setStoreFilter(key, ids.join(","));
  };

  return (
    <>
      {companyOptions.length > 1 && (
        <div className="flex items-center gap-2">
          <label
            id={`${idPrefix}-company-label`}
            htmlFor={`${idPrefix}-company`}
            className="text-xs text-slate-500"
          >
            Company
          </label>
          <div className="w-[220px]">
            <RightworkSelect
              id={`${idPrefix}-company`}
              value={companyId || ""}
              onChange={(e) => setCompanyId(Number(e.target.value))}
              options={companyOptions.map((c) => ({ value: c.id, label: c.company_name }))}
            />
          </div>
        </div>
      )}
      {storeOptions.length > 1 && (
        <div className="flex items-center gap-2">
          <label
            id={`${idPrefix}-stores-label`}
            htmlFor={`${idPrefix}-stores`}
            className="text-xs text-slate-500"
          >
            Stores
          </label>
          <div className="w-[260px]">
            <RightworkSelect
              id={`${idPrefix}-stores`}
              value={storeFilter.key}
              onChange={(e) => handleStoreFilterChange(e.target.value)}
              // A district can share its name with a store; say which was picked.
              renderValue={(key) =>
                key.startsWith("district:")
                  ? `District: ${key.slice("district:".length)}`
                  : key.startsWith("store:")
                  ? storeOptions.find((s) => `store:${s.id}` === key)?.store_name
                  : "All my stores"
              }
              options={storeFilterOptions}
            />
          </div>
        </div>
      )}
    </>
  );
}
